import React from "react";
import { lireVue } from "../../lib/vues-registre.js";
import { dessinPourForme } from "./dessins/index.jsx";
import { PhraseAbsence } from "./dessins/_commun";

// DessinVue — prend la vue VALIDÉE par lib/reponse-registres.js et ses données,
// et choisit le bon dessin.
//
// La vue arrive sous la forme `{ nom, args, titre, forme }` : le nom vient de
// la liste fermée du registre, les args ont déjà été filtrés (jamais de prose),
// `titre` et `forme` ont été recopiés depuis le registre — pas depuis le modèle.
//
// ══ DEUX GARDES, PAR CONSTRUCTION ═══════════════════════════════════════════
//
//   1. La forme est cherchée dans la liste FERMÉE des dessins ; une forme
//      inconnue ne tombe sur aucun dessin « par défaut ».
//   2. Si le nom est connu du registre, sa forme DOIT être celle qu'on nous
//      passe. Une vue « occupation » qu'on nous demanderait de dessiner en
//      grille de jours est un désaccord — on le dit, on ne dessine pas.
//
// ══ LE TITRE DIT CE QUI A ÉTÉ COMPRIS ═══════════════════════════════════════
//
// Registre : « une vue mal CHOISIE est pire qu'une absence ». Le titre affiché
// est celui du registre, suivi des identifiants reçus (« Évolution — lc2 · 12m »),
// pour que Dimitri voie ce qui a été compris, pas seulement ce qui a été dessiné.

/** Compose le titre affiché : le titre du registre + les args, dans l'ordre du registre. */
export function composerTitre(vue) {
  const base =
    typeof vue.titre === "string" && vue.titre.trim()
      ? vue.titre.trim()
      : "Vue";
  const desc = lireVue(vue.nom);
  const ordre = desc ? desc.args : Object.keys(vue.args || {});
  const valeurs = ordre
    .map((cle) => (vue.args ? vue.args[cle] : undefined))
    .filter((val) => val != null && String(val).trim() !== "")
    .map((val) => String(val).slice(0, 40));
  return valeurs.length ? `${base} — ${valeurs.join(" · ")}` : base;
}

// ⚠️ `onAller` TRAVERSE, il ne s'arrête pas ici. Règle Dimitri 2026-09-09 :
// « tout ce qui est affiché doit mener quelque part ; un dessin qui ne mène
// nulle part est une capture d'écran ». Le dessin ne connaît pas les onglets —
// il rend une INTENTION, l'écran décide.
// ⚠️ `onOublier` — même règle : la croix d'une consigne rend une INTENTION
// (`onOublier(id)`), l'écran appelle le levier existant. Seul le dessin
// « consignes » s'en sert ; les autres l'ignorent.
export default function DessinVue({ vue, donnees, onAller, onOublier, noms }) {
  if (!vue || typeof vue !== "object") {
    return <PhraseAbsence texte="aucune vue à dessiner" />;
  }
  const Dessin = dessinPourForme(vue.forme);
  if (!Dessin) {
    return (
      <PhraseAbsence
        texte={`forme de réponse inconnue${typeof vue.forme === "string" ? ` : ${vue.forme.slice(0, 40)}` : ""}`}
      />
    );
  }
  const desc = lireVue(vue.nom);
  if (desc && desc.forme !== vue.forme) {
    return (
      <PhraseAbsence
        titre={desc.titre}
        texte={`cette vue se dessine en « ${desc.forme} », pas en « ${vue.forme} » — rien dessiné`}
      />
    );
  }
  return (
    <div
      data-testid="dessin-vue"
      data-forme={vue.forme}
      data-nom={typeof vue.nom === "string" ? vue.nom : undefined}
    >
      <Dessin
        titre={composerTitre(vue)}
        donnees={donnees}
        onAller={onAller}
        onOublier={onOublier}
        noms={noms}
      />
    </div>
  );
}
